import { Yii } from "libraries/interfaces/Yii";
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { IView } from 'libraries/hotel/models/view';
import { ModalComponent } from 'libraries/ui_modal/modal.component';
import { IMenuDetails } from 'libraries/hotel/models/menu-details';
import { IOtherRoomsRequest, IQuoteRequest } from 'libraries/service/resources/hotel/ISearchParameters';
import { IAlternative, IHotelConfig } from 'libraries/hotel/models/hotel';
import { ICurrency } from 'libraries/service/resources/model/search/ICurrency';
import { ICurrencies, IGetPropertyResponse, IProperty } from 'libraries/service/resources/hotel/ISearchResponse';
import { IConfigSharedHotel } from 'libraries/hotel/models/config-shared-hotel';
import { wenji } from "libraries/interfaces/IWenji";
import { IChangeRoom } from "libraries/hotel/containers/hotels-list/services/IChangeRoom";
import { DetailType, IAccommodation, IDetailsOffer, IMealplan } from "libraries/hotel/models/details";
import { ILabels } from "libraries/interfaces/ILabels";
import { IMapConfiguration } from "libraries/interfaces/IMapConfiguration";
import { IGetQuotes } from "../hotel/hotel.component";
import { INotification } from "libraries/interfaces/INotification";
import { IHotelProperty } from "libraries/service/resources/model/search/hotel/IHotelProperty";
import { HotelInformationsModalService } from "./service/hotel-informations-modal.service";
import { ConfigSharedHotelService } from "libraries/service/resources/configuration/config-shared-hotel.service";
import { ErrorTracingService } from "libraries/service/error-tracing/error-tracing.service";
import { PropertyResourceService } from "libraries/service/resources/hotel/property-resource.service";
import { faExclamationTriangle, IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { EStayPermissions } from "libraries/enums/e-stay-permissions";
import { LanguageService } from "libraries/service/language/language.service";

interface ISelectedQuotationHotels {
    selected: string;
    hotels: {
        [key: string]: any;
    };
}

@Component({
    selector: 'hotel-informations-modal',
    templateUrl: './hotel-informations-modal.component.html',
    styleUrls: ['./hotel-informations-modal.component.scss']
})
export class HotelInformationsModalComponent implements OnChanges {
    @ViewChild('modal')
    public modal: ModalComponent;

    @Input()
    public selectedQuotation: ISelectedQuotationHotels;
    @Input()
    public view: IView;
    @Input()
    public travellers: any;
    @Input()
    public config: IHotelConfig;
    @Input()
    public menuDetailsItems: IMenuDetails[];
    @Input()
    public gppErrorNotification: INotification;
    @Input()
    public currency: ICurrency;
    @Input()
    public currencies: ICurrencies;
    @Input()
    public configSharedHotel: IConfigSharedHotel;
    @Input()
    public isPermModalAlternative: boolean;
    @Input()
    public searchResultAlternatives: IAlternative[];

    @Output()
    public readonly onGetOtherRooms: EventEmitter<IOtherRoomsRequest> = new EventEmitter();
    @Output()
    public readonly onChangeRoom: EventEmitter<IChangeRoom> = new EventEmitter();
    @Output()
    public readonly onGetQuotation: EventEmitter<IGetQuotes> = new EventEmitter();

    public selectedMenuDetailsItem: string = 'offers';

    public iconExclamation: IconDefinition = faExclamationTriangle;

    public readonly wording: ILabels = {
        generateQuotation: Yii.t('wui-shared-js', 'Génération de la cotation ...'),
        roomsLoading: Yii.t('wui-shared-js', 'Chargement des chambres en cours ...'),
        close: Yii.t('wui-shared-js', 'Fermer'),
        hotelsLoading: Yii.t('wui-shared-js', 'Recherche des hôtels en cours ...'),
        showMap: Yii.t('wui-shared-js', 'Voir sur la carte'),
        matchingHotels: Yii.t('wui-shared-js', 'hôtels correspondant à la recherche'),
        differentMealplansChosen: Yii.t("wui-shared-js", "Vous avez sélectionné des chambres avec des offres de repas différentes. Pour continuer, vous devez choisir des chambres avec la même offre de repas."),
        languagesWarning: Yii.t("wui-shared-js", "Une ou plusieurs descriptions ne sont pas disponibles dans la langue de votre environnement"),
        noGeoposition: Yii.t("wui-shared-js", "Aucune information de géolocalisation présente dans le dossier."),
    };

    public hasSelectedDifferentMealPlans: boolean = false;
    public mapConfiguration: IMapConfiguration = wenji.ui.map;
    public selectedHotelProperty: IHotelProperty;
    public preferGiataProperty: boolean;
    public isWithGiata: boolean;

    constructor(
        private readonly hotelInformationsModalService: HotelInformationsModalService,
        private readonly configSharedHotelService: ConfigSharedHotelService,
        private readonly errorTracingService: ErrorTracingService,
        private readonly propertyResourceService: PropertyResourceService,
        private readonly languageService: LanguageService,
    ) {
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes?.selectedQuotation?.currentValue && changes.selectedQuotation?.currentValue.selected) {
            this.selectedHotelProperty = {
                ...this.selectedQuotation.hotels[this.selectedQuotation.selected].property,
                geoposition: this.hotelInformationsModalService.getGeoposition(this.selectedQuotation.hotels[this.selectedQuotation.selected].property),
                descriptions: this.selectedQuotation.hotels[this.selectedQuotation.selected].description &&
                    this.selectedQuotation.hotels[this.selectedQuotation.selected].description.descriptions,
            };
            this.getConfig();
        }
    }

    public changeTab(tabCode: string): void {
        this.selectedMenuDetailsItem = tabCode;

        this.menuDetailsItems.map((item: IMenuDetails): IMenuDetails => {
            item.selected = item.code === tabCode;
            return item;
        });
    }

    public getOtherRooms(request: IOtherRoomsRequest): void {
        this.onGetOtherRooms.emit(request);
        const syscode: string = this.selectedHotelProperty?.alternatives?.find((alternative: IAlternative) => alternative.hostCode === request.hosts)?.syscode;
        if (this.selectedHotelProperty.alternatives && syscode) {
            this.propertyResourceService.getHostProperty(request.hosts, syscode).subscribe({
                next: (propertyResponse: IGetPropertyResponse): void => {
                    propertyResponse && propertyResponse.property && this.changeSelectedHotelProperty(propertyResponse.property);
                },
                error: (error: Error): void => {
                    this.errorTracingService.captureException(error);
                }
            });
        }
    }

    public getQuotation(request: IQuoteRequest): void {
        this.onGetQuotation.emit({request, hotelInformations: this.selectedQuotation.hotels[this.selectedQuotation.selected]});
    }

    public show(): void {
        this.selectedMenuDetailsItem = 'offers';
        this.modal.show();
    }

    public hide(): void {
        this.modal.hide();
    }

    public changeRoom(changeRoom: IChangeRoom): void {
        const newOffer: IDetailsOffer = changeRoom.offers.find((candidateOffer: IDetailsOffer): boolean => candidateOffer.xId === changeRoom.offerXid);

        const mealplans: Set<string> = new Set(Object.values(newOffer.detail)
            .filter((detail: IAccommodation | IMealplan): boolean => detail.xType === DetailType.Accomodation)
            .map((accomodation: IAccommodation): IMealplan => accomodation.mealplan)
            .map((mealplan: IMealplan): string => mealplan?.syscode));

        this.hasSelectedDifferentMealPlans = mealplans.size > 1;

        this.onChangeRoom.emit(changeRoom);
    }

    public hasDifferentLanguage(): boolean {
        return this.selectedHotelProperty ?
            this.hotelInformationsModalService.hasDifferentLanguage(this.selectedHotelProperty, this.languageService.userLanguage) :
            false;
    }

    public changeSelectedHotelProperty(property: IProperty): void {
        this.selectedHotelProperty = {
            ...this.hotelInformationsModalService.changeHotelPropertyAfterGetProperty(
                this.selectedHotelProperty as unknown as IProperty,
                property,
            ) as unknown as IHotelProperty
        };
    }

    public permission: typeof EStayPermissions = EStayPermissions;

    private getConfig(): void {
        this.configSharedHotelService.getConfiguration().subscribe({
            next: (sharedHotelConfig: IConfigSharedHotel): void => {
                this.preferGiataProperty = sharedHotelConfig.detail?.property.preferGiataProperty;
                this.isWithGiata = sharedHotelConfig.detail?.property.isWithGiata;
            },
            error: (error: Error): void => {
                this.errorTracingService.captureException(error);
            }
        });
    }
}
